// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useRef, useCallback } from 'react';
import { Home, Camera, Search, Menu } from 'lucide-react';

/**
 * Bottom Navigation Component
 * Fixed bottom nav bar with 4 main tabs
 * Visible on ALL views for easy navigation
 * Full accessibility: ARIA tablist, keyboard arrow nav, focus indicators
 */
const BottomNavigation = ({ activeTab, onTabChange }) => {
  const tabRefs = useRef([]);

  const tabs = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'scan', label: 'Scan', icon: Camera },
    { id: 'compare', label: 'Compare', icon: Search },
    { id: 'more', label: 'More', icon: Menu }
  ];

  const handleKeyDown = useCallback((e, index) => {
    let newIndex = index;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      newIndex = (index + 1) % tabs.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      newIndex = (index - 1 + tabs.length) % tabs.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      newIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      newIndex = tabs.length - 1;
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTabChange(tabs[index].id);
      return;
    } else {
      return;
    }
    tabRefs.current[newIndex]?.focus();
    onTabChange(tabs[newIndex].id);
  }, [onTabChange, tabs]);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-[60]"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-lg mx-auto">
        <div className="flex justify-around items-center h-16" role="tablist" aria-label="App sections">
          {tabs.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                ref={(el) => (tabRefs.current[index] = el)}
                role="tab"
                id={`tab-${tab.id}`}
                aria-selected={isActive}
                aria-controls={`panel-${tab.id}`}
                aria-label={`${tab.label} tab`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => onTabChange(tab.id)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 ${
                  isActive 
                    ? 'text-green-600' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon 
                  className={`w-6 h-6 mb-1 transition-transform duration-200 ${
                    isActive ? 'scale-110' : ''
                  }`} 
                  strokeWidth={isActive ? 2.5 : 2}
                  aria-hidden="true"
                />
                <span className={`text-xs font-medium ${
                  isActive ? 'font-semibold' : ''
                }`}>
                  {tab.label}
                </span>
                {isActive && (
                  <div className="absolute bottom-0 w-12 h-1 bg-green-600 rounded-t-full" aria-hidden="true" />
                )}
              </button>
            );
          })}
        </div>
      </div>
      {/* Safe area for iOS devices */}
      <div className="h-safe-area-inset-bottom bg-white" aria-hidden="true" />
    </nav>
  );
};

export default BottomNavigation;
